<script lang="ts">
	import { cookieConsent, updateCookiePreferences, type CookiePreferences } from '$lib/stores/cookies';
	import Button from '$lib/components/primitives/Button.svelte';
	import Icon from '$lib/components/primitives/Icon.svelte';
	import { _ } from 'svelte-i18n';
	
	let { onclose } = $props<{ onclose: () => void }>();
	
	// Local state for toggles
	let preferences = $state<CookiePreferences>({
		necessary: true, // Always enabled
		analytics: $cookieConsent ? $cookieConsent.preferences.analytics : false,
		marketing: $cookieConsent ? $cookieConsent.preferences.marketing : false,
		preferences: $cookieConsent ? $cookieConsent.preferences.preferences : false
	});
	
	function handleSave() {
		updateCookiePreferences(preferences);
		onclose();
	}
	
	function handleAcceptAll() {
		updateCookiePreferences({
			necessary: true,
			analytics: true,
			marketing: true,
			preferences: true
		});
		onclose();
	}
</script>

<div class="settings-header">
	<h2 class="settings-title">{$_('cookies.settings.title')}</h2>
	<button class="close-button" onclick={onclose} aria-label="Close">
		<Icon name="x" size={20} />
	</button>
</div>

<div class="settings-body">
	<p class="settings-intro">
		{$_('cookies.settings.intro')}
	</p>
	
	<div class="cookie-categories">
		<!-- Necessary Cookies -->
		<div class="cookie-category">
			<div class="category-header">
				<div class="category-info">
					<h3 class="category-title">{$_('cookies.categories.necessary.title')}</h3>
					<p class="category-description">
						{$_('cookies.categories.necessary.detailedDescription')}
					</p>
				</div>
				<div class="toggle-wrapper">
					<input 
						type="checkbox" 
						id="necessary" 
						checked={true}
						disabled
						class="toggle-input"
					/>
					<label for="necessary" class="toggle-label disabled">
						<span class="toggle-slider"></span>
					</label>
				</div>
			</div>
			<div class="category-details">
				<p class="details-label">{$_('cookies.settings.alwaysEnabled')}</p>
				<ul class="cookie-list">
					<li>{$_('cookies.features.sessionManagement')}</li>
					<li>{$_('cookies.features.authentication')}</li>
					<li>{$_('cookies.features.securityFeatures')}</li>
				</ul>
			</div>
		</div>
		
		<!-- Analytics Cookies -->
		<div class="cookie-category">
			<div class="category-header">
				<div class="category-info">
					<h3 class="category-title">{$_('cookies.categories.analytics.title')}</h3>
					<p class="category-description">
						{$_('cookies.categories.analytics.detailedDescription')}
					</p>
				</div>
				<div class="toggle-wrapper">
					<input 
						type="checkbox" 
						id="analytics" 
						bind:checked={preferences.analytics}
						class="toggle-input"
					/>
					<label for="analytics" class="toggle-label">
						<span class="toggle-slider"></span>
					</label>
				</div>
			</div>
			<div class="category-details">
				<ul class="cookie-list">
					<li>{$_('cookies.features.googleAnalytics')}</li>
					<li>{$_('cookies.features.pageViews')}</li>
					<li>{$_('cookies.features.timeOnPages')}</li>
					<li>{$_('cookies.features.browserInfo')}</li>
				</ul>
			</div>
		</div>
		
		<!-- Marketing Cookies -->
		<div class="cookie-category">
			<div class="category-header">
				<div class="category-info">
					<h3 class="category-title">{$_('cookies.categories.marketing.title')}</h3>
					<p class="category-description">
						{$_('cookies.categories.marketing.detailedDescription')}
					</p>
				</div>
				<div class="toggle-wrapper">
					<input 
						type="checkbox" 
						id="marketing" 
						bind:checked={preferences.marketing}
						class="toggle-input"
					/>
					<label for="marketing" class="toggle-label">
						<span class="toggle-slider"></span>
					</label>
				</div>
			</div>
			<div class="category-details">
				<ul class="cookie-list">
					<li>{$_('cookies.features.adTargeting')}</li>
					<li>{$_('cookies.features.campaignPerformance')}</li>
					<li>{$_('cookies.features.retargeting')}</li>
					<li>{$_('cookies.features.socialTracking')}</li>
				</ul>
			</div>
		</div>
		
		<!-- Preference Cookies -->
		<div class="cookie-category">
			<div class="category-header">
				<div class="category-info">
					<h3 class="category-title">{$_('cookies.categories.preferences.title')}</h3>
					<p class="category-description">
						{$_('cookies.categories.preferences.detailedDescription')}
					</p>
				</div>
				<div class="toggle-wrapper">
					<input 
						type="checkbox" 
						id="preferences" 
						bind:checked={preferences.preferences}
						class="toggle-input"
					/>
					<label for="preferences" class="toggle-label">
						<span class="toggle-slider"></span>
					</label>
				</div>
			</div>
			<div class="category-details">
				<ul class="cookie-list">
					<li>{$_('cookies.features.languagePrefs')}</li>
					<li>{$_('cookies.features.themeSettings')}</li>
					<li>{$_('cookies.features.displayPrefs')}</li>
					<li>{$_('cookies.features.timezone')}</li>
				</ul>
			</div>
		</div>
	</div>
</div>

<div class="settings-footer">
	<Button variant="ghost" onclick={onclose}>
		{$_('cookies.settings.cancel')}
	</Button>
	<div class="footer-actions">
		<Button variant="secondary" onclick={handleAcceptAll}>
			{$_('cookies.settings.acceptAll')}
		</Button>
		<Button variant="primary" onclick={handleSave}>
			{$_('cookies.settings.savePreferences')}
		</Button>
	</div>
</div>

<style>
	.settings-header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: var(--space-6);
		border-bottom: 1px solid var(--border-light);
	}
	
	.settings-title {
		font-size: var(--font-size-2xl);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
	}
	
	.close-button {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 32px;
		height: 32px;
		background: var(--bg-secondary);
		border: 1px solid var(--border-light);
		border-radius: var(--radius-md);
		color: var(--text-secondary);
		cursor: pointer;
		transition: var(--transition-colors);
	}
	
	.close-button:hover {
		background: var(--bg-tertiary);
		color: var(--text-primary);
	}
	
	.settings-body {
		padding: var(--space-6);
		max-height: 60vh;
		overflow-y: auto;
	}
	
	.settings-intro {
		font-size: var(--font-size-base);
		color: var(--text-secondary);
		margin-bottom: var(--space-6);
		line-height: var(--line-height-relaxed);
	}
	
	.cookie-categories {
		display: flex;
		flex-direction: column;
		gap: var(--space-6);
	}
	
	.cookie-category {
		padding: var(--space-6);
		background: var(--bg-secondary);
		border-radius: var(--radius-lg);
		border: 1px solid var(--border-light);
	}
	
	.category-header {
		display: flex;
		justify-content: space-between;
		align-items: flex-start;
		gap: var(--space-4);
		margin-bottom: var(--space-4);
	}
	
	.category-info {
		flex: 1;
	}
	
	.category-title {
		font-size: var(--font-size-lg);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin-bottom: var(--space-2);
	}
	
	.category-description {
		font-size: var(--font-size-sm);
		color: var(--text-secondary);
		line-height: var(--line-height-relaxed);
	}
	
	/* Toggle Switch */
	.toggle-wrapper {
		flex-shrink: 0;
	}
	
	.toggle-input {
		position: absolute;
		opacity: 0;
		pointer-events: none;
	}
	
	.toggle-label {
		position: relative;
		display: inline-block;
		width: 48px;
		height: 24px;
		background: var(--bg-tertiary);
		border-radius: 12px;
		cursor: pointer;
		transition: var(--transition-colors);
		border: 1px solid var(--border-medium);
	}
	
	.toggle-label.disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}
	
	.toggle-slider {
		position: absolute;
		top: 2px;
		left: 2px;
		width: 18px;
		height: 18px;
		background: white;
		border-radius: 50%;
		transition: transform 0.2s ease;
		box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
	}
	
	.toggle-input:checked + .toggle-label {
		background: var(--brand-primary);
		border-color: var(--brand-primary);
	}
	
	.toggle-input:checked + .toggle-label .toggle-slider {
		transform: translateX(24px);
	}
	
	.toggle-input:disabled + .toggle-label {
		background: var(--brand-primary);
		border-color: var(--brand-primary);
	}
	
	.category-details {
		padding-top: var(--space-4);
		border-top: 1px solid var(--border-light);
	}
	
	.details-label {
		font-size: var(--font-size-xs);
		color: var(--text-tertiary);
		text-transform: uppercase;
		letter-spacing: 0.05em;
		margin-bottom: var(--space-2);
	}
	
	.cookie-list {
		list-style: none;
		padding: 0;
		margin: 0;
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
		gap: var(--space-2);
	}
	
	.cookie-list li {
		font-size: var(--font-size-sm);
		color: var(--text-secondary);
		padding-left: var(--space-4);
		position: relative;
	}
	
	.cookie-list li::before {
		content: "•";
		position: absolute;
		left: 0;
		color: var(--text-tertiary);
	}
	
	.settings-footer {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: var(--space-6);
		border-top: 1px solid var(--border-light);
		background: var(--bg-secondary);
	}
	
	.footer-actions {
		display: flex;
		gap: var(--space-3);
	}
	
	/* Responsive */
	@media (max-width: 640px) {
		.settings-body {
			max-height: 50vh;
		}
		
		.category-header {
			flex-direction: column;
			gap: var(--space-3);
		}
		
		.toggle-wrapper {
			align-self: flex-start;
		}
		
		.settings-footer {
			flex-direction: column;
			gap: var(--space-3);
		}
		
		.footer-actions {
			width: 100%;
		}
		
		.footer-actions :global(.btn) {
			flex: 1;
		}
	}
</style>